"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Color = void 0;
const react_1 = require("react");
const q_play_core_1 = require("@nordicscreen/q-play-core");
class Color {
    constructor(props) {
        var _a;
        this.hue = 0;
        this.saturation = 100;
        this.value = 100;
        this.red = 255;
        this.green = 0;
        this.blue = 0;
        this.alpha = 1;
        this.hex = "#FF0000";
        this.hexa = props.hexa;
        this.debouncer = new q_play_core_1.Debouncer(props.callback, 500);
        this.updatePreview = props.updatePreview;
        this.dataKey = (_a = props.dataKey) !== null && _a !== void 0 ? _a : "";
        if (props.test === true) {
            this.colorGradientRef = null;
            this.colorGradientCircleRef = null;
            this.hueSliderRef = null;
            this.colorHueRef = null;
            this.alphaSliderRef = null;
            this.colorAlphaRef = null;
            this.hexInputRef = null;
            this.alphaInputRef = null;
        }
        else {
            this.colorGradientRef = (0, react_1.useRef)(null);
            this.colorGradientCircleRef = (0, react_1.useRef)(null);
            this.hueSliderRef = (0, react_1.useRef)(null);
            this.colorHueRef = (0, react_1.useRef)(null);
            this.alphaSliderRef = (0, react_1.useRef)(null);
            this.colorAlphaRef = (0, react_1.useRef)(null);
            this.hexInputRef = (0, react_1.useRef)(null);
            this.alphaInputRef = (0, react_1.useRef)(null);
        }
        // Calculates the Alpha, RGB, HSV values from the hexa value given in the constructor
        let hexa = props.hexa;
        if (hexa.substring(0, 1) === "#") {
            hexa = hexa.slice(1);
        }
        // Splits the hexa code into hex and alpha values.
        this.hex = "#" + hexa.slice(0, 6).toUpperCase();
        if (hexa.length > 6) {
            const alphaHexCode = hexa.slice(6, 8);
            this.alpha = parseFloat((parseInt(alphaHexCode, 16) / 255).toPrecision(2));
        }
        let hex = "";
        if (this.hex[0] === '#') {
            hex = this.hex.slice(1, this.hex.length);
        }
        this.red = parseInt(hex.substring(0, 2), 16);
        this.green = parseInt(hex.substring(2, 4), 16);
        this.blue = parseInt(hex.substring(4, 6), 16);
        const red = this.red / 255;
        const green = this.green / 255;
        const blue = this.blue / 255;
        const cmax = Math.max(red, green, blue);
        const cmin = Math.min(red, green, blue);
        const delta = cmax - cmin;
        let hue = 0;
        let saturation = 0;
        if (delta) {
            if (cmax === red) {
                hue = ((green - blue) / delta);
            }
            if (cmax === green) {
                hue = 2 + (blue - red) / delta;
            }
            if (cmax === blue) {
                hue = 4 + (red - green) / delta;
            }
            if (cmax)
                saturation = delta / cmax;
        }
        let newHue = 60 * hue | 0;
        if (newHue < 0)
            newHue += 360;
        this.hue = newHue;
        this.saturation = saturation * 100 | 0;
        this.value = cmax * 100 | 0;
    }
    ;
    handleCallback() {
        this.updatePreview(this.getHexa());
        const rgba = this.getRGBA();
        const rgbaMethod = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${rgba.alpha})`;
        this.debouncer.debounce(rgbaMethod, this.dataKey); // should return rgba() function instead of #rrggbbaa
    }
    ;
    revertColorChange() {
        let hexa = this.hexa;
        if (hexa.substring(0, 1) === "#") {
            hexa = hexa.slice(1);
        }
        // Splits the hexa code into hex and alpha values.
        this.hex = "#" + hexa.slice(0, 6).toUpperCase();
        if (hexa.length > 6) {
            const alphaHexCode = hexa.slice(6, 8);
            this.alpha = parseFloat((parseInt(alphaHexCode, 16) / 255).toPrecision(2));
        }
        else {
            this.alpha = 1;
        }
        // Update RGB and HSV values.
        this.updateRGBFromHex();
        this.updateHSVFromRGB();
        // Update the rendering to match the current color.
        this.setAlphaSliderBackgroundColor();
        this.updateAlphaSelector();
        this.updateColorSelectors();
        this.handleCallback();
    }
    ;
    /*========== Methods to set Color Properties ==========*/
    setHSV(hue, saturation, value) {
        this.hue = hue;
        this.saturation = saturation;
        this.value = value;
    }
    ;
    setRGB(red, green, blue) {
        this.red = red;
        this.green = green;
        this.blue = blue;
    }
    ;
    setHex(hex) {
        if (this.alpha === 0) {
            this.alpha = 1;
            this.updateAlphaSelector();
        }
        this.hex = hex;
        this.setAlphaSliderBackgroundColor();
        this.handleCallback();
    }
    ;
    setAlpha(alpha) {
        this.alpha = alpha;
        this.handleCallback();
    }
    ;
    setHue(hue) {
        this.hue = hue;
        this.setColorGradientBackground();
        this.updateRGBFromHSV();
        this.updateHexFromRGB();
    }
    ;
    /*========== Get Methods ==========*/
    getHex() {
        return this.hex;
    }
    ;
    getRGBA() {
        return { red: this.red, green: this.green, blue: this.blue, alpha: this.alpha };
    }
    ;
    getHueColor() {
        let H = this.hue / 60;
        let X = (1 - Math.abs(H % 2 - 1));
        X = (X) * 255 | 0;
        let RGB;
        if (H >= 0 && H < 1) {
            RGB = { red: 255, green: X, blue: 0 };
        }
        else if (H >= 1 && H < 2) {
            RGB = { red: X, green: 255, blue: 0 };
        }
        else if (H >= 2 && H < 3) {
            RGB = { red: 0, green: 255, blue: X };
        }
        else if (H >= 3 && H < 4) {
            RGB = { red: 0, green: X, blue: 255 };
        }
        else if (H >= 4 && H < 5) {
            RGB = { red: X, green: 0, blue: 255 };
        }
        else {
            RGB = { red: 255, green: 0, blue: X };
        }
        // Converts the calculated rgb into hex
        let r = RGB.red.toString(16);
        let g = RGB.green.toString(16);
        let b = RGB.blue.toString(16);
        if (RGB.red < 16)
            r = '0' + r;
        if (RGB.green < 16)
            g = '0' + g;
        if (RGB.blue < 16)
            b = '0' + b;
        return ('#' + r + g + b).toUpperCase();
    }
    ;
    getOpacityWithoutPercent() {
        return (this.alpha * 100).toFixed(0);
    }
    ;
    getHexa() {
        let alpha = (Math.round(this.alpha * 255)).toString(16).toUpperCase();
        alpha = alpha.length == 1 ? "0" + alpha : alpha;
        return this.getHex() + alpha;
    }
    ;
    /*========== Update color formats Methods ==========*/
    updateRGBFromHSV() {
        const H = this.hue / 60;
        const sat = this.saturation / 100;
        const value = this.value / 100;
        let C = sat * value;
        let X = C * (1 - Math.abs(H % 2 - 1));
        let m = value - C;
        const precision = 255;
        C = (C + m) * precision | 0;
        X = (X + m) * precision | 0;
        m = m * precision | 0;
        if (H >= 0 && H < 1) {
            this.setRGB(C, X, m);
            return;
        }
        if (H >= 1 && H < 2) {
            this.setRGB(X, C, m);
            return;
        }
        if (H >= 2 && H < 3) {
            this.setRGB(m, C, X);
            return;
        }
        if (H >= 3 && H < 4) {
            this.setRGB(m, X, C);
            return;
        }
        if (H >= 4 && H < 5) {
            this.setRGB(X, m, C);
            return;
        }
        if (H >= 5 && H < 6) {
            this.setRGB(C, m, X);
            return;
        }
    }
    ;
    updateHSVFromRGB() {
        const red = this.red / 255;
        const green = this.green / 255;
        const blue = this.blue / 255;
        const cmax = Math.max(red, green, blue);
        const cmin = Math.min(red, green, blue);
        const delta = cmax - cmin;
        let hue = 0;
        let saturation = 0;
        if (delta) {
            if (cmax === red) {
                hue = ((green - blue) / delta);
            }
            if (cmax === green) {
                hue = 2 + (blue - red) / delta;
            }
            if (cmax === blue) {
                hue = 4 + (red - green) / delta;
            }
            if (cmax)
                saturation = delta / cmax;
        }
        let newHue = 60 * hue | 0;
        if (newHue < 0)
            newHue += 360;
        let newSaturation = saturation * 100 | 0;
        let newValue = cmax * 100 | 0;
        this.setHSV(newHue, newSaturation, newValue);
    }
    ;
    updateRGBFromHex() {
        let hex = this.hex;
        if (hex[0] === '#') {
            hex = hex.slice(1, hex.length);
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        this.setRGB(r, g, b);
    }
    ;
    updateHexFromRGB() {
        let r = this.red.toString(16);
        let g = this.green.toString(16);
        let b = this.blue.toString(16);
        if (this.red < 16)
            r = '0' + r;
        if (this.green < 16)
            g = '0' + g;
        if (this.blue < 16)
            b = '0' + b;
        const hex = '#' + r + g + b;
        this.setHex(hex.toUpperCase());
    }
    /*************************************************************************/
    //						Update input elements
    /*************************************************************************/
    removeInputFocus() {
        var _a, _b, _c, _d;
        (_b = (_a = this.alphaInputRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.blur();
        (_d = (_c = this.hexInputRef) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.blur();
    }
    updateColorPickerCircle(x, y) {
        var _a, _b, _c;
        if (this.colorGradientCircleRef && this.alphaSliderRef) {
            const RGBA = this.getRGBA();
            const cssRGBAColor = `rgba(${RGBA.red}, ${RGBA.green}, ${RGBA.blue}, ${RGBA.alpha})`;
            const pickerOffset = 5;
            (_a = this.colorGradientCircleRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty("left", x - pickerOffset + 'px');
            (_b = this.colorGradientCircleRef.current) === null || _b === void 0 ? void 0 : _b.style.setProperty("top", y - pickerOffset + 'px');
            (_c = this.alphaSliderRef.current) === null || _c === void 0 ? void 0 : _c.style.setProperty("background-color", cssRGBAColor);
        }
    }
    ;
    updateAlphaSliderCircle(y) {
        var _a, _b;
        if (this.alphaSliderRef) {
            const RGBA = this.getRGBA();
            const cssRGBAColor = `rgba(${RGBA.red}, ${RGBA.green}, ${RGBA.blue}, ${RGBA.alpha})`;
            (_a = this.alphaSliderRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty("top", y + 'px');
            (_b = this.alphaSliderRef.current) === null || _b === void 0 ? void 0 : _b.style.setProperty("background-color", cssRGBAColor);
        }
    }
    ;
    updateHueSliderCircle(y) {
        var _a, _b, _c;
        if (this.alphaSliderRef && this.hueSliderRef) {
            const RGBA = this.getRGBA();
            const cssRGBAColor = `rgba(${RGBA.red}, ${RGBA.green}, ${RGBA.blue}, ${RGBA.alpha})`;
            (_a = this.hueSliderRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty("top", y + 'px');
            (_b = this.hueSliderRef.current) === null || _b === void 0 ? void 0 : _b.style.setProperty("background-color", this.getHueColor());
            (_c = this.alphaSliderRef.current) === null || _c === void 0 ? void 0 : _c.style.setProperty("background-color", cssRGBAColor);
        }
    }
    ;
    setAlphaSliderBackgroundColor() {
        var _a;
        if (this.colorAlphaRef) {
            (_a = this.colorAlphaRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty("background", `linear-gradient(to top, ${this.hex}00 0%, ${this.hex} 100%)`);
        }
    }
    ;
    setColorGradientBackground() {
        var _a;
        if (this.colorGradientRef) {
            (_a = this.colorGradientRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty("background-color", this.getHueColor());
        }
    }
    ;
    updateColorSelectors() {
        if (this.colorGradientRef && this.colorGradientRef.current) {
            // The width and height should be the same
            const size = this.colorGradientRef.current.clientWidth;
            const x = (this.saturation * size / 100) | 0;
            const y = size - (this.value * size / 100) | 0;
            this.updateColorPickerCircle(x, y);
        }
        this.updateHueSelector();
        this.setColorGradientBackground();
    }
    ;
    updateHueSelector() {
        if (this.colorHueRef && this.colorHueRef.current) {
            const size = this.colorHueRef.current.clientHeight;
            const pos = (this.hue * size / 360) | 0;
            this.updateHueSliderCircle(pos);
        }
    }
    ;
    updateAlphaSelector() {
        if (this.colorAlphaRef && this.colorAlphaRef.current) {
            const size = this.colorAlphaRef.current.clientHeight;
            const pos = ((1 - this.alpha) * size) | 0;
            this.updateAlphaSliderCircle(pos);
        }
    }
    ;
}
exports.Color = Color;
;
